import React from "react";
import ReactApexChart from "react-apexcharts";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import LinearProgress from "@material-ui/core/LinearProgress";
import { makeStyles } from "@material-ui/core/styles";
import { P, COMPARE_LABEL, CSS_VARS } from "./constants";

// Elevated white card surface used by Kpi tiles and the generic Card. MUI's
// default Paper shadow/radius don't match the dashboard, so the surface tokens
// (14px radius, layered shadow, hairline border) are overridden here.
const useSurfaceStyles = makeStyles({
  surface: {
    background: "var(--color-background-primary)",
    border: "1px solid var(--color-border-tertiary)",
    boxShadow: "0 6px 10px rgba(0,0,0,.08), 0 0 6px rgba(0,0,0,.05)",
  },
  kpi: { borderRadius: 14, padding: "14px 16px" },
  card: { borderRadius: 14, padding: 18 },
});

function Qualifiers({ values = [] }) {
  if (!values.length) return null;

  return (
    <span style={{display:"inline-flex",gap:4,marginLeft:"auto",paddingLeft:6,verticalAlign:"middle"}}>
      {values.map((value) => (
        <Badge key={value} color="#666666">{value}</Badge>
      ))}
    </span>
  );
}

export function Kpi({ label, value, delta, up, invertColor, period, qualifiers = [] }) {
  const s = useSurfaceStyles();
  const isGood = invertColor ? !up : up;
  return (
    <Paper elevation={0} className={`${s.surface} ${s.kpi}`}>
      <Typography variant="subtitle2" component="div" style={{display:"flex",alignItems:"center",color:"var(--color-text-secondary)",letterSpacing:"0.01em"}}>
        {label}<Qualifiers values={qualifiers} />
      </Typography>
      <Typography variant="h6" component="div" style={{fontWeight:700,letterSpacing:"-.03em",color:P.headline}}>{value}</Typography>
      {delta !== undefined && (
        <Typography variant="body2" component="div" style={{display:"flex",alignItems:"center",gap:3,marginTop:4,color:isGood ? "#2E7D32" : "#FF3B30"}}>
          <span>{up ? "▲" : "▼"}</span><span style={{fontWeight:600}}>{Math.abs(delta)}%</span>
          <span style={{color:"var(--color-text-tertiary)",marginLeft:2}}>vs {COMPARE_LABEL[period]}</span>
        </Typography>
      )}
    </Paper>
  );
}

export function Card({ title, subtitle, children, action, qualifiers = [] }) {
  const s = useSurfaceStyles();
  return (
    <Paper elevation={0} className={`${s.surface} ${s.card}`}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{marginBottom:12}}>
        <div style={{flex:1}}>
          <Typography variant="subtitle1" component="div" style={{display:"flex",alignItems:"center",fontWeight:600,color:"var(--color-text-primary)"}}>
            {title}<Qualifiers values={qualifiers} />
          </Typography>
          {subtitle && <Typography variant="body2" component="div" style={{color:"var(--color-text-secondary)",marginTop:2}}>{subtitle}</Typography>}
        </div>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

export function Badge({ children, color, bg }) {
  // text color -> matching soft tint (indigo text gets a light-indigo wash,
  // not green, to avoid a blue-on-green clash)
  const bgMap = {"#3EAC4D":"#E6F4E8","#2E7D32":"#E6F4E8","#FF3B30":"#FFECEB","#3127B8":"#ECEBF8","#241C8A":"#ECEBF8","#666666":"#F2F3F5"};
  const bgColor = bg || bgMap[color] || "#F2F3F5";
  return <span style={{display:"inline-flex",alignItems:"center",padding:"3px 8px",borderRadius:5,fontSize:13,fontWeight:600,background:bgColor,color:color,letterSpacing:"0.01em"}}>{children}</span>;
}

const useProgBarStyles = makeStyles({
  root: { height: 5, borderRadius: 3, backgroundColor: "var(--color-background-secondary)" },
  bar: { borderRadius: 3, backgroundColor: ({ color }) => color || P.am },
});

export function ProgBar({ pct, color }) {
  const c = useProgBarStyles({ color });
  return <LinearProgress variant="determinate" value={Math.min(100, pct)} classes={{ root: c.root, bar: c.bar }} />;
}

// Resolved theme tokens — ApexCharts renders to SVG attributes that need real
// color values rather than CSS var() references.
const AXIS_TXT = CSS_VARS["--color-text-secondary"];
const GRID_CLR = CSS_VARS["--color-border-tertiary"];

// Builds an HTML tooltip matching the look of the dashboard cards (mirrors the
// former recharts <CT /> custom tooltip).
function apexTooltip({ series, dataPointIndex, w }) {
  const label = w.globals.categoryLabels?.[dataPointIndex] ?? w.globals.labels?.[dataPointIndex] ?? "";
  const rows = w.globals.seriesNames.map((name, i) => {
    const v = series[i]?.[dataPointIndex];
    if (v == null) return "";
    const color = w.globals.colors[i];
    return (
      `<div style="display:flex;gap:8px;align-items:center;color:#666666;margin-bottom:3px;">`
        + `<span style="width:8px;height:8px;border-radius:2px;background:${color};display:inline-block;"></span>`
        + `<span style="font-size:13px;">${name}</span>`
        + `<span style="font-weight:600;color:#1A1A1A;margin-left:auto;font-family:monospace;font-size:13px;">${v.toLocaleString()}</span>`
      + `</div>`
    );
  }).join("");
  return (
    `<div style="background:#FFFFFF;border:1px solid #D2D6DA;border-radius:10px;padding:10px 14px;font-size:13px;box-shadow:0 4px 16px rgba(0,0,0,0.08);">`
      + `<div style="font-weight:600;margin-bottom:6px;color:#1A1A1A;font-size:13px;">${label}</div>`
      + rows
    + `</div>`
  );
}

function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const f = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(f, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

// Declarative ApexCharts wrapper for the Insights line/area charts. Mirrors the
// shape that the recharts <LineChart>/<AreaChart> components were fed:
//   data      — array of points
//   xKey      — key on each point used for the category (x) axis
//   series    — [{ key, name, color, width?, dash?, opacity?, fill? }]
//   yaxis     — single { formatter } OR an array of axis groups
//               [{ series:[names], formatter, opposite }] for dual-axis charts
//   type      — "line" | "area"
export function InsightsChart({ type = "line", data, xKey, series, yaxis, height = "100%" }) {
  const categories = data.map(pt => pt[xKey]);
  const apexSeries = series.map(s => ({ name: s.name, data: data.map(pt => pt[s.key]) }));
  // Bake per-series opacity into the stroke color so dimmed lines render
  // correctly (ApexCharts has no per-series stroke opacity).
  const colors = series.map(s => (s.opacity != null && s.opacity < 1 ? hexToRgba(s.color, s.opacity) : s.color));
  const widths = series.map(s => s.width ?? 2);
  const dashArray = series.map(s => s.dash ?? 0);
  const seriesOpacity = series.map(s => s.opacity ?? 1);
  const hasFill = series.some(s => s.fill);

  const axisLabelStyle = { colors: AXIS_TXT, fontSize: "12px" };

  let yaxisOpt;
  if (Array.isArray(yaxis)) {
    // Dual (or multi) axis: every series in a group shares one explicit scale so
    // current/prior lines stay aligned, with only the first entry showing labels.
    yaxisOpt = [];
    yaxis.forEach(group => {
      const vals = data.flatMap(pt => group.series.map(name => {
        const s = series.find(x => x.name === name);
        return s ? pt[s.key] : null;
      })).filter(v => v != null);
      const max = Math.max(...vals);
      group.series.forEach((name, i) => {
        yaxisOpt.push({
          seriesName: name,
          opposite: !!group.opposite,
          show: i === 0,
          min: 0, max, forceNiceScale: true,
          labels: { formatter: group.formatter, style: axisLabelStyle },
          axisBorder: { show: false },
          axisTicks: { show: false },
        });
      });
    });
  } else {
    yaxisOpt = {
      min: 0,
      max: yaxis?.max,
      forceNiceScale: true,
      labels: { formatter: yaxis?.formatter, style: axisLabelStyle },
      axisBorder: { show: false },
      axisTicks: { show: false },
    };
  }

  const options = {
    chart: {
      type,
      height,
      fontFamily: CSS_VARS["--font-sans"],
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      parentHeightOffset: 0,
    },
    colors,
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: widths, dashArray },
    fill: hasFill
      ? { type: "solid", colors: series.map(s => s.fill || s.color), opacity: seriesOpacity.map(o => o * 0.45) }
      : { type: "solid", opacity: seriesOpacity },
    markers: { size: 0, hover: { sizeOffset: 3 } },
    grid: { borderColor: GRID_CLR, strokeDashArray: 3, padding: { top: 4, right: 12, left: 12, bottom: 0 } },
    legend: { show: false },
    xaxis: {
      categories,
      labels: { style: axisLabelStyle },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: yaxisOpt,
    tooltip: { custom: apexTooltip, shared: true, intersect: false },
  };

  return <ReactApexChart type={type} options={options} series={apexSeries} height={height} width="100%" />;
}
